import type { HolidayPackageSummary } from '@/src/types/holidays'

/**
 * Free-text search over the package cards.
 *
 * The haystack is what the card itself shows — destination, tagline, tags and airline — so a
 * result always explains itself: every match is visible on the card the reader gets back.
 */

/** Lower-cased and stripped of diacritics, so "Tbilisi" answers to "tbilisi" and "Saariselkä" to
 *  "saariselka". The Arabic copy normalises harmlessly, since it has no combining marks here. */
const normalise = (value: string): string =>
    value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')

const haystackFor = (summary: HolidayPackageSummary): string =>
    normalise(
        [summary.destinationName, summary.tagline ?? '', summary.airline?.name ?? '', ...summary.tags].join(' ')
    )

/**
 * Every word of the query must appear somewhere on the card, in any order — "almaty air" finds the
 * Almaty departure flown by Air Arabia, and a stray double space does not lose the match.
 */
export function searchHolidayPackages(
    packages: HolidayPackageSummary[],
    query: string
): HolidayPackageSummary[] {
    const words = normalise(query).split(' ').filter(Boolean)
    if (!words.length) return packages

    return packages.filter(summary => {
        const haystack = haystackFor(summary)
        return words.every(word => haystack.includes(word))
    })
}
